import type { ApiClient, LegacyKuduResponseEnvelope } from '@/api/client.types';
import type { ThemeSetting } from '@/theme/themeTypes';
import { isThemeSetting } from '@/theme/themeScript';

import type {
  ProfileData,
  ProfileService,
  RawGetUserResponse,
  RawKuduProfile,
  RawUserSettings,
  RawKuduUser,
  RawUpdateUserResponse,
  UpdateDismissedTourParams,
  UpdateDisplayThemeParams,
  UpdateProfileParams,
} from './profileTypes';

type KuduClient = Pick<ApiClient, 'invokeKuduMethod'>;

interface GetUserRequest {
  user_id: string;
}

interface UpdateUserRequest {
  dismissed_tours?: string[];
  profile?: {
    first_name: string;
    institute_official_id: string;
    is_instructor: boolean;
    last_name: string;
    preferred_email: string;
  };
  settings?: {
    display: {
      theme: ThemeSetting;
    };
  };
  user_id: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => normalizeString(item))
    .filter((item): item is string => item !== null);
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function isProfileComplete(
  profile: Pick<ProfileData, 'firstName' | 'lastName'> | null | undefined,
): boolean {
  return Boolean(profile?.firstName.trim() && profile.lastName.trim());
}

function getProfileString(
  profile: RawKuduProfile | null | undefined,
  snakeKey: keyof RawKuduProfile,
  camelKey: keyof RawKuduProfile,
): string {
  return (
    normalizeString(profile?.[snakeKey]) ??
    normalizeString(profile?.[camelKey]) ??
    ''
  );
}

function readInstructorFlag(profile: RawKuduProfile | null | undefined): boolean {
  if (typeof profile?.is_instructor === 'boolean') {
    return profile.is_instructor;
  }

  if (typeof profile?.isInstructor === 'boolean') {
    return profile.isInstructor;
  }

  return false;
}

function readAssistantFlag(profile: RawKuduProfile | null | undefined): boolean {
  if (typeof profile?.is_assistant === 'boolean') {
    return profile.is_assistant;
  }

  if (typeof profile?.isAssistant === 'boolean') {
    return profile.isAssistant;
  }

  return false;
}

function unwrapLegacyEnvelope<T>(
  response: T | LegacyKuduResponseEnvelope<T> | null | undefined,
): T | null {
  if (!response || !isRecord(response)) {
    return null;
  }

  const responseRecord = response as Record<string, unknown>;

  if ('Response' in responseRecord) {
    return unwrapLegacyEnvelope(
      (response as unknown as LegacyKuduResponseEnvelope<T>).Response,
    );
  }

  return response as T;
}

function normalizeRawProfile(
  profile: RawKuduProfile | null | undefined,
): Record<string, unknown> | null {
  return isRecord(profile) ? { ...profile } : null;
}

function normalizeRawSettings(
  settings: RawUserSettings | null | undefined,
): Record<string, unknown> | null {
  return isRecord(settings) ? { ...settings } : null;
}

export function normalizeDisplayTheme(
  settings: RawUserSettings | null | undefined,
): ThemeSetting | null {
  const rawTheme = settings?.display?.theme ?? settings?.displayTheme ?? null;
  return isThemeSetting(rawTheme) ? rawTheme : null;
}

export function normalizeProfileData(
  response:
    | RawGetUserResponse
    | RawUpdateUserResponse
    | LegacyKuduResponseEnvelope<RawGetUserResponse | RawUpdateUserResponse>
    | null
    | undefined,
  fallbackUserId: string,
): ProfileData {
  const payload = unwrapLegacyEnvelope<
    RawGetUserResponse | RawUpdateUserResponse
  >(response);
  const rawUser = isRecord(payload?.user)
    ? (payload.user as RawKuduUser)
    : null;
  const rawProfile = isRecord(rawUser?.profile)
    ? (rawUser.profile as RawKuduProfile)
    : null;
  const rawSettings = isRecord(rawUser?.settings)
    ? (rawUser.settings as RawUserSettings)
    : null;
  const backendUserId = normalizeString(rawUser?.id);
  const isAssistant = readAssistantFlag(rawProfile);

  // TODO: Convert legacy numeric user IDs to displayable email addresses.
  // Some users have numeric IDs from the old system that cannot be shown
  // as a login email. A migration or lookup is needed.
  const loginEmail = backendUserId && isValidEmail(backendUserId)
    ? backendUserId
    : null;

  return {
    courseIds: normalizeStringArray(rawUser?.course_ids ?? rawUser?.courseIds),
    dismissedTours: normalizeStringArray(
      rawUser?.dismissed_tours ?? rawUser?.dismissedTours,
    ),
    firstName: getProfileString(rawProfile, 'first_name', 'firstName'),
    instituteOfficialId: getProfileString(
      rawProfile,
      'institute_official_id',
      'instituteOfficialId',
    ),
    ...(isAssistant ? { isAssistant } : {}),
    isInstructor: readInstructorFlag(rawProfile),
    lastName: getProfileString(rawProfile, 'last_name', 'lastName'),
    loginEmail,
    preferredEmail: getProfileString(
      rawProfile,
      'preferred_email',
      'preferredEmail',
    ),
    rawProfile: normalizeRawProfile(rawProfile),
    rawSettings: normalizeRawSettings(rawSettings),
    settings: {
      displayTheme: normalizeDisplayTheme(rawSettings),
    },
    userId: fallbackUserId,
  };
}

export function patchProfileDisplayTheme(
  profile: ProfileData | null | undefined,
  theme: ThemeSetting,
): ProfileData | undefined {
  if (!profile) {
    return undefined;
  }

  return {
    ...profile,
    rawSettings: {
      ...(profile.rawSettings ?? {}),
      display: {
        ...(isRecord(profile.rawSettings?.display)
          ? profile.rawSettings.display
          : {}),
        theme,
      },
    },
    settings: {
      ...profile.settings,
      displayTheme: theme,
    },
  };
}

export function patchProfileDismissedTour(
  profile: ProfileData | null | undefined,
  tourId: string,
): ProfileData | undefined {
  if (!profile || profile.dismissedTours.includes(tourId)) {
    return profile ?? undefined;
  }

  return {
    ...profile,
    dismissedTours: [...profile.dismissedTours, tourId],
  };
}

function normalizeUpdateUserResponse(
  response:
    | RawUpdateUserResponse
    | LegacyKuduResponseEnvelope<RawUpdateUserResponse>
    | null
    | undefined,
  fallbackUserId: string,
): ProfileData | null {
  const payload = unwrapLegacyEnvelope<RawUpdateUserResponse>(response);

  return isRecord(payload?.user)
    ? normalizeProfileData(payload, fallbackUserId)
    : null;
}

// The answer to a profile save. `UpdateUser` returns the saved user record
// itself, where the other responses wrap it in `user`, so this reads either.
// It is the server's copy: its course list is what the server holds, not what
// this app had cached before the save. A bare record is taken only when it is
// the user that was saved and carries a profile; anything else returns null
// and the caller patches its cached copy.
function normalizeSavedProfileResponse(
  response:
    | RawUpdateUserResponse
    | RawKuduUser
    | LegacyKuduResponseEnvelope<RawUpdateUserResponse | RawKuduUser>
    | null
    | undefined,
  userId: string,
): ProfileData | null {
  const payload = unwrapLegacyEnvelope<RawUpdateUserResponse | RawKuduUser>(
    response,
  );

  if (!isRecord(payload)) {
    return null;
  }

  if ('user' in payload && isRecord(payload.user)) {
    return normalizeProfileData(payload, userId);
  }

  const savedUser = payload as RawKuduUser;

  return normalizeString(savedUser.id) === userId && isRecord(savedUser.profile)
    ? normalizeProfileData({ user: savedUser }, userId)
    : null;
}

export function createProfileService(client: KuduClient): ProfileService {
  return {
    async getProfile({ userId }) {
      const response = await client.invokeKuduMethod<
        RawGetUserResponse | LegacyKuduResponseEnvelope<RawGetUserResponse>,
        GetUserRequest
      >('GetUser', {
        user_id: userId,
      });

      return normalizeProfileData(response, userId);
    },
    async updateDismissedTour(params: UpdateDismissedTourParams) {
      const response = await client.invokeKuduMethod<
        | RawUpdateUserResponse
        | LegacyKuduResponseEnvelope<RawUpdateUserResponse>
        | null
        | undefined,
        UpdateUserRequest
      >('UpdateUser', {
        dismissed_tours: [params.tourId],
        user_id: params.userId,
      });

      return normalizeUpdateUserResponse(response, params.userId);
    },
    async updateDisplayTheme(params: UpdateDisplayThemeParams) {
      const response = await client.invokeKuduMethod<
        | RawUpdateUserResponse
        | LegacyKuduResponseEnvelope<RawUpdateUserResponse>
        | null
        | undefined,
        UpdateUserRequest
      >('UpdateUser', {
        settings: {
          display: {
            theme: params.theme,
          },
        },
        user_id: params.userId,
      });

      return normalizeUpdateUserResponse(response, params.userId);
    },
    async updateProfile(params: UpdateProfileParams) {
      const response = await client.invokeKuduMethod<
        | RawUpdateUserResponse
        | RawKuduUser
        | LegacyKuduResponseEnvelope<RawUpdateUserResponse | RawKuduUser>
        | null
        | undefined,
        UpdateUserRequest
      >('UpdateUser', {
        profile: {
          first_name: params.profile.firstName,
          institute_official_id: params.profile.instituteOfficialId,
          is_instructor: params.profile.isInstructor,
          last_name: params.profile.lastName,
          preferred_email: params.profile.preferredEmail,
        },
        user_id: params.userId,
      });

      return normalizeSavedProfileResponse(response, params.userId);
    },
  };
}
